import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axiosInstance from "../../../axiosInstance";
import slugify from "slugify";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);


const MySwal = withReactContent(Swal);
 
const generateSlug = (name) => {
  return slugify(name || "", {
    lower: true,
    strict: true,
    trim: true,
  });
};

const generateISO2 = (name) => {
  const iso = countries.getAlpha2Code(name, "en");
  return iso ? iso.toUpperCase() : "";
};


export default function AdminCountriesSinglePage() {
  const [loading, setLoading] = useState(true);

  // list
  const [countries, setCountries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // add form
  const [addLoading, setAddLoading] = useState(false);
const [addForm, setAddForm] = useState({
  name: "",
  currency: "",
  taxType: "NONE",
  status: true,
  iso2: "",
  slug: "",
});

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    name: "",
    currency: "",
    taxType: "NONE",
    status: true,
  });

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/admin/countries?page=${currentPage}&limit=${limit}&search=${encodeURIComponent(searchTerm)}`
      );

      // adjust to your backend response key
      const list = res.data.countries || res.data.Countries || [];
      setCountries(list);
      setCurrentPage(res.data.currentPage || currentPage);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
      toast.error("Error fetching countries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  const onSearch = () => {
    setCurrentPage(1);
    fetchCountries();
  };

  // ---------------- ADD ----------------
  const handleAddChange = (e) => {
  const { name, value } = e.target;

  if (name === "name") {
    const slug = generateSlug(value);
    const iso2 = generateISO2(value);

    setAddForm((prev) => ({
      ...prev,
      name: value,
      slug,
      iso2,
    }));
    return;
  }

  setAddForm((prev) => ({
    ...prev,
    [name]: value,
  }));
};


  const addCountry = async () => {
    if (!addForm.name.trim()) return toast.error("Please enter country name");

    try {
      setAddLoading(true);

     await axiosInstance.post("/admin/country", {
  name: addForm.name.trim(),
  currency: addForm.currency.trim(),
  taxType: addForm.taxType,
  status: addForm.status,
  iso2: addForm.iso2,
  slug: addForm.slug,
});

      toast.success("Country added!");
      setAddForm({ name: "", currency: "", taxType: "NONE", status: true });
      setCurrentPage(1);
      fetchCountries();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error adding country");
    } finally {
      setAddLoading(false);
    }
  };

  // ---------------- EDIT ----------------
const openEdit = (row) => {
  setEditForm({
    _id: row._id,
    name: row.name,
    currency: row.currency || "",
    taxType: row.taxType || "NONE",
    status: row.status === true,
    iso2: row.iso2 || "",
    slug: row.slug || "",
  });
  setEditOpen(true);
};

 const handleEditChange = (e) => {
  const { name, value } = e.target;

  if (name === "name") {
    const slug = generateSlug(value);
    const iso2 = generateISO2(value);

    setEditForm((prev) => ({
      ...prev,
      name: value,
      slug,
      iso2,
    }));
    return;
  }

  setEditForm((prev) => ({
    ...prev,
    [name]: value,
  }));
};

  const updateCountry = async () => {
    if (!editForm.name.trim()) return toast.error("Please enter country name");

    try {
      setEditLoading(true);
      await axiosInstance.put(`/admin/country/${editForm._id}`, {
        name: editForm.name.trim(),
        currency: editForm.currency.trim(),
        taxType: editForm.taxType,
        status: editForm.status,
      });

      toast.success("Country updated!");
      setEditOpen(false);
      fetchCountries();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error updating country");
    } finally {
      setEditLoading(false);
    }
  };

  // ---------------- DELETE ----------------
  const deleteCountry = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this country!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        await axiosInstance.delete(`/admin/country/${id}`);
        toast.success("Country deleted!");
        fetchCountries();
      } catch (err) {
        toast.error(err?.response?.data?.message || "Error deleting country");
      }
    });
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap">
            <div className="mb-3 mb-lg-0">
              <h1 className="h4">Countries (Add / Edit / Delete)</h1>
            </div>
          </div>
        </div>

        {/* ADD FORM */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Country Name</label>
                <input
                  className="form-control"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddChange}
                  placeholder="India"
                />
              </div>

              <div className="col-md-2">
  <label className="form-label">ISO2</label>
  <input
    className="form-control"
    value={addForm.iso2}
    readOnly
  />
</div>

<div className="col-md-3">
  <label className="form-label">Slug</label>
  <input
    className="form-control"
    value={addForm.slug}
    readOnly
  />
</div>


              <div className="col-md-3">
                <label className="form-label">Currency</label>
                <input
                  className="form-control"
                  name="currency"
                  value={addForm.currency}
                  onChange={handleAddChange}
                  placeholder="INR"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Tax Type</label>
                <select className="form-control" name="taxType" value={addForm.taxType} onChange={handleAddChange}>
                  <option value="NONE">NONE</option>
                  <option value="GST">GST</option>
                  <option value="VAT">VAT</option>
                  <option value="SALES_TAX">SALES_TAX</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">Status</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      checked={addForm.status === true}
                      onChange={() => setAddForm((p) => ({ ...p, status: true }))}
                    />
                    <label className="form-check-label text-success">Active</label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      checked={addForm.status === false}
                      onChange={() => setAddForm((p) => ({ ...p, status: false }))}
                    />
                    <label className="form-check-label text-danger">Inactive</label>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <button className="btn btn-primary" type="button" onClick={addCountry} disabled={addLoading}>
                  {addLoading ? "Adding..." : "Add Country"}
                </button>
                <small className="ms-3 text-muted">
                  * ISO2 and slug will be auto-generated by backend.
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* LIST + SEARCH */}
        <div className="card">
          <div className="table-responsive py-4">
            <div className="datatable-top px-3">
              <div className="datatable-dropdown">
                <label>
                  <select
                    className="datatable-selector"
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                  </select>{" "}
                  entries per page
                </label>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="datatable-search">
                  <input
                    className="datatable-input"
                    type="text"
                    placeholder="Search name / iso2 / slug..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button className="btn btn-primary" onClick={onSearch}>
                  Search
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-4">Loading...</div>
            ) : (
              <>
                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>ISO2</th>
                      <th>Slug</th>
                      <th>Currency</th>
                      <th>Tax Type</th>
                      <th>Status</th>
                      <th style={{ width: 220 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map((row) => (
                      <tr key={row._id}>
                        <td>{row.name}</td>
                        <td>{row.iso2 || "-"}</td>
                        <td>{row.slug || "-"}</td>
                        <td>{row.currency || "-"}</td>
                        <td>{row.taxType || "NONE"}</td>
                        <td>
                          <span className={`badge ${row.status ? "bg-success" : "bg-danger"}`}>
                            {row.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-primary me-2" onClick={() => openEdit(row)}>
                            Edit
                          </button>
                          <button className="btn btn-danger" onClick={() => deleteCountry(row._id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!countries.length && (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          No countries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="datatable-bottom px-3">
                  <nav className="datatable-pagination">
                    <ul className="datatable-pagination-list">
                      <li className={`datatable-pagination-list-item ${currentPage === 1 ? "datatable-hidden" : "datatable-active"}`}>
                        <button
                          className="datatable-pagination-list-item-link"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          ‹
                        </button>
                      </li>

                      <li>
                        <div className="datatable-info py-1 px-3">
                          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </div>
                      </li>

                      <li className={`datatable-pagination-list-item ${currentPage === totalPages ? "datatable-hidden" : "datatable-active"}`}>
                        <button
                          className="datatable-pagination-list-item-link"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>

        {/* EDIT MODAL */}
        {editOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Country</h5>
                  <button type="button" className="btn-close" onClick={() => setEditOpen(false)} />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Country Name</label>
                      <input className="form-control" name="name" value={editForm.name} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-3">
  <label className="form-label">ISO2</label>
  <input className="form-control" value={editForm.iso2} readOnly />
</div>

<div className="col-md-3">
  <label className="form-label">Slug</label>
  <input className="form-control" value={editForm.slug} readOnly />
</div>


                    <div className="col-md-3">
                      <label className="form-label">Currency</label>
                      <input className="form-control" name="currency" value={editForm.currency} onChange={handleEditChange} />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Tax Type</label>
                      <select className="form-control" name="taxType" value={editForm.taxType} onChange={handleEditChange}>
                        <option value="NONE">NONE</option>
                        <option value="GST">GST</option>
                        <option value="VAT">VAT</option>
                        <option value="SALES_TAX">SALES_TAX</option>
                      </select>
                    </div>

                    <div className="col-md-12">
                      <label className="form-label">Status</label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            checked={editForm.status === true}
                            onChange={() => setEditForm((p) => ({ ...p, status: true }))}
                          />
                          <label className="form-check-label text-success">Active</label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            checked={editForm.status === false}
                            onChange={() => setEditForm((p) => ({ ...p, status: false }))}
                          />
                          <label className="form-check-label text-danger">Inactive</label>
                        </div>
                      </div>
 
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditOpen(false)} disabled={editLoading}>
                    Close
                  </button>
                  <button className="btn btn-primary" onClick={updateCountry} disabled={editLoading}>
                    {editLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}